import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import CloseIcon from '@mui/icons-material/Close';
import { getFp1BatteryHealth, type GetBatteryHealthParams } from '../fp1.api';
import type { Fp1BatteryHealthDevice } from '../fp1.types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ── Styled ───────────────────────────────────────────────────────────────

const FilterBar = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
}));

// ── Helpers ──────────────────────────────────────────────────────────────

// Maps DataGrid column fields to the backend's sort_by keys. Only these columns
// are sortable; everything else is locked (sortable: false on the column def).
const FIELD_TO_SORT: Record<string, GetBatteryHealthParams['sort_by']> = {
    organization_name: 'organization',
    athlete_name: 'athlete',
    drain_per_hour: 'drain_per_hour',
    upload_count: 'upload_count',
    last_activity_date: 'last_activity_date',
    last_deployed_at: 'last_deployed_at',
};

function formatDrainRate(rate: number | null): string {
    if (rate === null) return '--';
    return `${(rate * 100).toFixed(2)}%/hr`;
}

// Drain is a fraction of the pod's OWN full battery consumed per hour, so expected
// runtime = 1 / drain_per_hour hours — independent of the pack's mAh capacity.
function formatBatteryLife(rate: number | null): string {
    if (rate === null || rate <= 0) return '--';
    return formatHours(1 / rate);
}

function formatHours(hours: number | null): string {
    if (hours === null || hours <= 0) return '--';
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
}

function formatStatus(s: string) {
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Health chip ───────────────────────────────────────────────────────────

const HealthChip: React.FC<{ health: string; drainPerHour: number | null }> = ({
    health,
    drainPerHour,
}) => {
    if (health === 'no_data') {
        return (
            <Chip
                label="No Data"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500, fontSize: '0.75rem' }}
            />
        );
    }

    const color = health === 'good' ? 'success' : health === 'fair' ? 'warning' : 'error';
    const icon =
        health === 'good' ? (
            <BatteryChargingFullIcon sx={{ fontSize: 14 }} />
        ) : health === 'fair' ? (
            <Battery60Icon sx={{ fontSize: 14 }} />
        ) : (
            <BatteryAlertIcon sx={{ fontSize: 14 }} />
        );
    const label = health === 'good' ? 'Good' : health === 'fair' ? 'Fair' : 'Poor';

    return (
        <Tooltip
            title={
                drainPerHour !== null
                    ? `~${formatBatteryLife(drainPerHour)} battery life (${formatDrainRate(
                          drainPerHour,
                      )})`
                    : ''
            }
        >
            <Chip
                icon={icon}
                label={label}
                size="small"
                color={color}
                sx={{ fontWeight: 500, fontSize: '0.75rem' }}
            />
        </Tooltip>
    );
};

// ── Warranty dialog ──────────────────────────────────────────────────────

interface WarrantyDialogProps {
    open: boolean;
    device: Fp1BatteryHealthDevice | null;
    onClose: () => void;
}

const WarrantyDialog: React.FC<WarrantyDialogProps> = ({ open, device, onClose }) => {
    const [snack, setSnack] = useState('');

    if (!device) return null;

    const athleteName = device.athlete_name?.trim() || 'Athlete';
    const email = device.athlete_email ?? '';
    const phone = device.athlete_phone ?? '';
    const serial = device.serial_number;

    const emailSubject = encodeURIComponent(`FiyrPod Warranty Replacement – Device ${serial}`);
    const emailBody = encodeURIComponent(
        `Hi ${athleteName},\n\n` +
            `We noticed that your FiyrPod (Serial: ${serial}) may be experiencing ` +
            `battery drain issues. We'd like to offer you a warranty replacement device ` +
            `at no cost to you.\n\n` +
            `Please reply to this email to arrange your replacement.\n\n` +
            `Thank you,\nFiyrPod Support`,
    );

    const smsBody = encodeURIComponent(
        `Hi ${athleteName}, your FiyrPod (${serial}) may have a battery issue. ` +
            `We'd like to send you a free warranty replacement. Reply YES to proceed.`,
    );

    const copyToClipboard = (text: string, label: string) => {
        void navigator.clipboard.writeText(text).then(() => setSnack(`${label} copied`));
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Device Details & Warranty
                        </Typography>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Device info */}
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        DEVICE
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 1.5,
                            mb: 3,
                        }}
                    >
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Serial Number
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                                >
                                    {serial}
                                </Typography>
                                <Tooltip title="Copy serial">
                                    <IconButton
                                        size="small"
                                        sx={{ p: '2px', opacity: 0.5, '&:hover': { opacity: 1 } }}
                                        onClick={() => copyToClipboard(serial, 'Serial number')}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Status
                            </Typography>
                            <Typography variant="body2">{formatStatus(device.status)}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Battery Health
                            </Typography>
                            <Box mt={0.5}>
                                <HealthChip
                                    health={device.battery_health}
                                    drainPerHour={device.drain_per_hour}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Expected Battery Life
                            </Typography>
                            <Typography variant="body2">
                                {formatBatteryLife(device.drain_per_hour)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Drain / Hour (measured)
                            </Typography>
                            <Typography variant="body2">
                                {formatDrainRate(device.drain_per_hour)}
                            </Typography>
                        </Box>
                        {device.upload_count > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Uploads Analyzed
                                </Typography>
                                <Typography variant="body2">
                                    {device.upload_count} (last 30 days)
                                </Typography>
                            </Box>
                        )}
                        {device.last_activity_date && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Last Activity
                                </Typography>
                                <Typography variant="body2">{device.last_activity_date}</Typography>
                            </Box>
                        )}
                        {device.last_deployed_at && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Last Deployed
                                </Typography>
                                <Typography variant="body2">{device.last_deployed_at}</Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Most-recent session (the basis for the battery-life value) */}
                    {device.last_session_battery_pct !== null ? (
                        <>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                LAST SESSION
                            </Typography>
                            <Box sx={{ mb: 2.5 }}>
                                <Typography variant="body2">
                                    <b>{device.last_session_battery_pct}%</b> battery used
                                    {device.last_session_duration_min !== null
                                        ? ` over ${device.last_session_duration_min} min`
                                        : ''}
                                    {device.last_session_date
                                        ? ` on ${device.last_session_date}`
                                        : ''}
                                    .
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Battery life: <b>{formatBatteryLife(device.drain_per_hour)}</b>
                                    {device.sessions_averaged > 0
                                        ? ` (averaged over ${device.sessions_averaged} session${
                                              device.sessions_averaged === 1 ? '' : 's'
                                          })`
                                        : ''}
                                </Typography>
                                {device.sessions_averaged === 1 && (
                                    <Typography variant="caption" color="warning.main">
                                        Based on a single session — may not be representative.
                                    </Typography>
                                )}
                            </Box>
                        </>
                    ) : null}

                    {/* Organization */}
                    {device.organization_name && (
                        <>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                ORGANIZATION
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2.5 }}>
                                {device.organization_name}
                            </Typography>
                        </>
                    )}

                    {/* Athlete contact */}
                    {(email || phone || device.athlete_name) && (
                        <>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                ATHLETE CONTACT
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 1.5,
                                    mb: 3,
                                }}
                            >
                                {device.athlete_name && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Name
                                        </Typography>
                                        <Typography variant="body2">
                                            {device.athlete_name}
                                        </Typography>
                                    </Box>
                                )}
                                {email && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Email
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" noWrap title={email}>
                                                {email}
                                            </Typography>
                                            <Tooltip title="Copy email">
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        p: '2px',
                                                        opacity: 0.5,
                                                        '&:hover': { opacity: 1 },
                                                    }}
                                                    onClick={() => copyToClipboard(email, 'Email')}
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                )}
                                {phone && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Phone
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2">{phone}</Typography>
                                            <Tooltip title="Copy phone">
                                                <IconButton
                                                    size="small"
                                                    sx={{
                                                        p: '2px',
                                                        opacity: 0.5,
                                                        '&:hover': { opacity: 1 },
                                                    }}
                                                    onClick={() => copyToClipboard(phone, 'Phone')}
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}

                    {/* Warranty actions */}
                    {email || phone ? (
                        <>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                WARRANTY REPLACEMENT OUTREACH
                            </Typography>
                            <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                                Clicking below opens a pre-filled draft. Review and send from your
                                email/SMS client.
                            </Alert>
                            <Box display="flex" gap={1.5} flexWrap="wrap">
                                {email && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        href={`mailto:${email}?subject=${emailSubject}&body=${emailBody}`}
                                        target="_blank"
                                    >
                                        Draft Warranty Email
                                    </Button>
                                )}
                                {phone && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        href={`sms:${phone}?body=${smsBody}`}
                                    >
                                        Draft Warranty SMS
                                    </Button>
                                )}
                            </Box>
                        </>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem' }}>
                            No athlete contact information available for this device.
                        </Alert>
                    )}
                </DialogContent>
            </Dialog>
            <Snackbar
                open={!!snack}
                autoHideDuration={3000}
                onClose={() => setSnack('')}
                message={snack}
            />
        </>
    );
};

// ═════════════════════════════════════════════════════════════════════════
// ── Main Component ───────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════

const Fp1BatteryHealthTab: React.FC = () => {
    const theme = useTheme();

    const [devices, setDevices] = useState<Fp1BatteryHealthDevice[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
    const [params, setParams] = useState<GetBatteryHealthParams>({ page: 1, per_page: 50 });

    const [serialSearch, setSerialSearch] = useState('');
    const [healthFilter, setHealthFilter] = useState('');
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const [selectedDevice, setSelectedDevice] = useState<Fp1BatteryHealthDevice | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [healthCounts, setHealthCounts] = useState({ good: 0, fair: 0, poor: 0, no_data: 0 });

    const fetchData = useCallback(async (p: GetBatteryHealthParams) => {
        setIsLoading(true);
        setFetchError(null);
        try {
            const res = await getFp1BatteryHealth(p);
            setDevices(res.data?.data ?? []);
            setTotalRows(res.data?.meta?.total ?? 0);
            const counts = (res.data?.meta as any)?.health_counts;
            if (counts) {
                setHealthCounts({
                    good: counts.good ?? 0,
                    fair: counts.fair ?? 0,
                    poor: counts.poor ?? 0,
                    no_data: counts.no_data ?? 0,
                });
            }
        } catch (err: any) {
            console.error('Failed to fetch battery health data', err);
            const status = err?.response?.status;
            const message = err?.response?.data?.message ?? err?.message ?? 'Unknown error';
            setFetchError(`Error ${status ?? ''}: ${message}`);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData({ page: 1, per_page: 50 });
    }, [fetchData]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const newParams: GetBatteryHealthParams = {
            page: 1,
            per_page: pagination.pageSize,
            serial_number: serialSearch.trim() || undefined,
            health: (healthFilter as GetBatteryHealthParams['health']) || undefined,
            sort_by: params.sort_by,
            sort_dir: params.sort_dir,
        };
        setParams(newParams);
        setPagination({ ...pagination, page: 0 });
        fetchData(newParams);
    };

    const handlePaginationChange = (model: GridPaginationModel) => {
        setPagination(model);
        const newParams = { ...params, page: model.page + 1, per_page: model.pageSize };
        setParams(newParams);
        fetchData(newParams);
    };

    const handleSortChange = (model: GridSortModel) => {
        setSortModel(model);
        const first = model[0];
        const sort_by = first ? FIELD_TO_SORT[first.field] : undefined;
        let sort_dir: GetBatteryHealthParams['sort_dir'];
        if (sort_by) {
            sort_dir = first?.sort === 'desc' ? 'desc' : 'asc';
            // Expected Battery Life is the inverse of drain_per_hour (life = 1/drain),
            // so flip the direction sent to the backend: ascending life = shortest
            // life (worst pods) first, which is what users expect when prioritizing.
            if (first?.field === 'drain_per_hour') {
                sort_dir = sort_dir === 'asc' ? 'desc' : 'asc';
            }
        }
        const newParams: GetBatteryHealthParams = {
            ...params,
            page: 1,
            sort_by,
            sort_dir,
        };
        setParams(newParams);
        setPagination((prev) => ({ ...prev, page: 0 }));
        fetchData(newParams);
    };

    const gridSx = {
        border: 'none',
        '& .MuiDataGrid-cell:focus': { outline: 'none' },
        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'action.hover',
            borderRadius: 0,
        },
        '& .MuiDataGrid-row:hover': {
            bgcolor: (t: any) => alpha(t.palette.primary.main, 0.04),
        },
        '& .health-row-poor': {
            bgcolor: (t: any) => alpha(t.palette.error.main, 0.06),
        },
        '& .health-row-fair': {
            bgcolor: (t: any) => alpha(t.palette.warning.main, 0.06),
        },
    };

    const columns: GridColDef<Fp1BatteryHealthDevice>[] = [
        {
            field: 'serial_number',
            headerName: 'Serial Number',
            width: 200,
            sortable: false,
            renderCell: (p) => (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {p.value}
                    </Typography>
                    {p.value && (
                        <Tooltip title="Copy serial number">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigator.clipboard.writeText(p.value as string);
                                }}
                                sx={{ p: '2px', opacity: 0.5, '&:hover': { opacity: 1 } }}
                            >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
        {
            field: 'status',
            headerName: 'FP1 Status',
            width: 130,
            sortable: false,
            renderCell: (p) => (
                <Chip
                    label={formatStatus(p.value)}
                    size="small"
                    color={
                        p.value === 'deployed'
                            ? 'info'
                            : p.value === 'in_stock'
                            ? 'success'
                            : p.value === 'maintenance'
                            ? 'warning'
                            : p.value === 'defective'
                            ? 'error'
                            : 'default'
                    }
                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
            ),
        },
        {
            field: 'organization_name',
            headerName: 'Organization',
            width: 220,
            sortable: true,
            renderCell: (p) => (
                <Typography variant="body2" noWrap title={p.value || undefined}>
                    {p.value || '--'}
                </Typography>
            ),
        },
        {
            field: 'athlete_name',
            headerName: 'Athlete',
            width: 200,
            sortable: true,
            renderCell: (p) => (
                <Typography variant="body2" noWrap title={p.value || undefined}>
                    {p.value || '--'}
                </Typography>
            ),
        },
        {
            field: 'battery_health',
            headerName: 'Battery Health',
            width: 140,
            sortable: false,
            renderCell: (p) => <HealthChip health={p.value} drainPerHour={p.row.drain_per_hour} />,
        },
        {
            field: 'drain_per_hour',
            headerName: 'Expected Battery Life',
            width: 170,
            sortable: true,
            renderCell: (p) => (
                <Tooltip title={p.value !== null ? formatDrainRate(p.value) : ''}>
                    <Typography variant="body2">{formatBatteryLife(p.value)}</Typography>
                </Tooltip>
            ),
        },
        {
            field: 'upload_count',
            headerName: 'Uploads (30d)',
            width: 120,
            sortable: true,
            type: 'number',
            renderCell: (p) => (
                <Typography variant="body2" color={p.value === 0 ? 'text.disabled' : undefined}>
                    {p.value}
                </Typography>
            ),
        },
        {
            field: 'last_activity_date',
            headerName: 'Last Activity',
            width: 130,
            sortable: true,
            renderCell: (p) => (
                <Typography variant="body2" color="text.secondary">
                    {p.value ?? '--'}
                </Typography>
            ),
        },
        {
            field: 'last_deployed_at',
            headerName: 'Last Deployed',
            width: 130,
            sortable: true,
            renderCell: (p) => (
                <Typography variant="body2" color="text.secondary">
                    {p.value ?? '--'}
                </Typography>
            ),
        },
        {
            field: 'firmware_version',
            headerName: 'Firmware',
            width: 110,
            sortable: false,
            renderCell: (p) => (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {p.value || '--'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (p) => (
                <Tooltip title="View Details & Warranty">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDevice(p.row);
                            setDialogOpen(true);
                        }}
                    >
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    // Summary counts
    // Global counts come from meta.health_counts (all pods, not just this page)
    const goodCount = healthCounts.good;
    const fairCount = healthCounts.fair;
    const poorCount = healthCounts.poor;
    const noDataCount = healthCounts.no_data;

    return (
        <Box>
            {/* Summary bar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                {[
                    { label: 'Good', count: goodCount, color: theme.palette.success.main },
                    { label: 'Fair', count: fairCount, color: theme.palette.warning.main },
                    { label: 'Poor', count: poorCount, color: theme.palette.error.main },
                    { label: 'No Data', count: noDataCount, color: theme.palette.text.disabled },
                ].map(({ label, count, color }) => (
                    <Paper
                        key={label}
                        variant="outlined"
                        sx={{
                            px: 2.5,
                            py: 1.5,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minWidth: 130,
                            borderColor: alpha(color, 0.3),
                            bgcolor: alpha(color, 0.04),
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, lineHeight: 1.2, color }}
                            >
                                {count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {label}
                            </Typography>
                        </Box>
                    </Paper>
                ))}
                <Paper
                    variant="outlined"
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 200,
                        bgcolor: 'transparent',
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Expected: ~8h 00m
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Baseline battery life on a full charge
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            {/* Filters */}
            <FilterBar onSubmit={handleSearch}>
                <TextField
                    size="small"
                    label="Serial Number"
                    value={serialSearch}
                    onChange={(e) => setSerialSearch(e.target.value)}
                    sx={{ width: 200 }}
                    placeholder="Search serial..."
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Battery Health</InputLabel>
                    <Select
                        value={healthFilter}
                        onChange={(e) => setHealthFilter(e.target.value)}
                        label="Battery Health"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="good">Good (≥ 6h 40m)</MenuItem>
                        <MenuItem value="fair">Fair (3h 20m–6h 40m)</MenuItem>
                        <MenuItem value="poor">Poor ({'<'} 3h 20m)</MenuItem>
                        <MenuItem value="no_data">No Data</MenuItem>
                    </Select>
                </FormControl>
                <Button type="submit" variant="contained" startIcon={<SearchIcon />} size="medium">
                    Search
                </Button>
            </FilterBar>

            {/* Top pagination row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    {totalRows === 0
                        ? '0 results'
                        : `${pagination.page * pagination.pageSize + 1}–${Math.min(
                              (pagination.page + 1) * pagination.pageSize,
                              totalRows,
                          )} of ${totalRows}`}
                </Typography>
                <IconButton
                    size="small"
                    disabled={pagination.page === 0}
                    onClick={() =>
                        handlePaginationChange({ ...pagination, page: pagination.page - 1 })
                    }
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    disabled={(pagination.page + 1) * pagination.pageSize >= totalRows}
                    onClick={() =>
                        handlePaginationChange({ ...pagination, page: pagination.page + 1 })
                    }
                >
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Error display */}
            {fetchError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load battery health data: {fetchError}
                </Alert>
            )}

            {/* Grid */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <DataGridPremium
                    loading={isLoading}
                    rows={devices}
                    columns={columns}
                    getRowId={(row) => row.uuid}
                    getRowClassName={(p) =>
                        p.row.battery_health === 'poor'
                            ? 'health-row-poor'
                            : p.row.battery_health === 'fair'
                            ? 'health-row-fair'
                            : ''
                    }
                    rowCount={totalRows}
                    pagination
                    paginationMode="server"
                    paginationModel={pagination}
                    onPaginationModelChange={handlePaginationChange}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortChange}
                    pageSizeOptions={[25, 50, 100]}
                    disableColumnMenu
                    checkboxSelection={false}
                    autoHeight
                    sx={gridSx}
                    onRowClick={(p) => {
                        setSelectedDevice(p.row);
                        setDialogOpen(true);
                    }}
                />
            </Paper>

            <WarrantyDialog
                open={dialogOpen}
                device={selectedDevice}
                onClose={() => setDialogOpen(false)}
            />
        </Box>
    );
};

export default Fp1BatteryHealthTab;
